import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * Custom hook for template management — state, loading, CRUD.
 * Extracted from App.js.
 */
export const useTemplates = (user, effectiveRole) => {
  const [templates, setTemplates] = useState({ templates: [], categories: {} });
  const [userTemplates, setUserTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');

  // Load system templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data);
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    };
    loadTemplates();
  }, []);

  // Load user templates
  useEffect(() => {
    const loadUserTemplates = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API}/user/templates`, { withCredentials: true });
          setUserTemplates(response.data.templates || []);
        } catch (error) {
          console.error('Failed to load user templates:', error);
        }
      } else {
        setUserTemplates([]);
      }
    };
    loadUserTemplates();
  }, [user]);

  // Save as template
  const saveAsTemplate = useCallback(async (notes, letterContext, letterType, patientStatus, specialties, imagingOrders, setShowAuthModal) => {
    if (!user) { toast.error('Please sign in to save templates'); setShowAuthModal(true); return; }
    if (!newTemplateName.trim()) { toast.error('Please enter a template name'); return; }
    try {
      const response = await axios.post(`${API}/user/templates`, {
        name: newTemplateName, description: newTemplateDesc, notes_template: notes,
        context_template: letterContext, letter_type: letterType, patient_status: patientStatus,
        specialties: specialties.filter(s => s), imaging_types: imagingOrders.filter(i => i.type)
      }, { withCredentials: true });
      setUserTemplates(prev => [response.data, ...prev]);
      setShowSaveTemplateModal(false);
      setNewTemplateName('');
      setNewTemplateDesc('');
      toast.success('Template saved!');
    } catch (error) {
      toast.error('Failed to save template');
    }
  }, [user, newTemplateName, newTemplateDesc]);

  // Delete user template
  const deleteUserTemplate = useCallback(async (templateId) => {
    try {
      await axios.delete(`${API}/user/templates/${templateId}`, { withCredentials: true });
      setUserTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  }, []);

  return {
    templates, setTemplates,
    userTemplates, setUserTemplates,
    selectedTemplate, setSelectedTemplate,
    showTemplates, setShowTemplates,
    showSaveTemplateModal, setShowSaveTemplateModal,
    newTemplateName, setNewTemplateName,
    newTemplateDesc, setNewTemplateDesc,
    saveAsTemplate, deleteUserTemplate,
  };
};

export default useTemplates;
